"use client"

import clsx from "clsx"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import moment from "moment"
import React, { useEffect, useRef } from "react"

import { mapContentfulToButtonProps } from "@/helpers/utils"

import Button, { ContentfulButton } from "../../Button"
import PageGrid from "../../PageGrid"

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger)

type TimelineProps = {
  items: {
    heading: string
    date: string
    button: ContentfulButton
  }[]
}

const ImpactTimeline: React.FC<TimelineProps> = ({ items }) => {
  const animationRootScope = useRef(null)
  const tl = useRef(gsap.timeline())
  const bgRef = useRef(null)

  // Color gradient array for background transition
  const gradientColors = [
    "#F9F9FA",
    "#E7F3FB",
    "#D9EDFB",
    "#C8E6FB",
    "#BAE1FC",
    "#A9DAFD",
    "#99D4FD",
    "#87CDFE",
    "#73C5FF",
  ]

  const formatDate = (dateString: string): string =>
    moment(dateString).format("MMMM YYYY")

  const sortedItems = [...items].sort(
    (a, b) => moment(a.date).valueOf() - moment(b.date).valueOf(),
  )

  useEffect(() => {
    // -- DOM elements ready for animation --
    // -- Use gsap matchMedia for responsive targeting --
    const mm = gsap.matchMedia(animationRootScope)
    mm.add(
      {
        isMobile: "(max-width: 500px)",
        isDesktop: "(min-width: 501px)",
      },
      (context) => {
        if (context.conditions) {
          const { isMobile } = context.conditions
          tl.current = gsap.timeline({ paused: true })

          const ticks: gsap.DOMTarget[] = gsap.utils.toArray(
            ".tick-mark",
            animationRootScope.current,
          )

          tl.current.to(ticks, {
            ease: "power1.in",
            scaleX: 1,
            // duration tick-mark
            duration: 6,
            stagger: {
              each: 0.4,
              repeat: 1,
              yoyo: true,
              from: 0,
            },
          })

          tl.current.to(
            ".time-label",
            {
              ease: "steps(1)",
              opacity: 1,
              // keep in sync
              duration: 0.4,
              stagger: {
                // keep in sync
                each: 0.4,
                repeat: 1,
                yoyo: true,
                from: 0,
              },
            },
            6 - 0.4,
          )
          tl.current.to(
            ".heading-link",
            {
              ease: "steps(1)",
              opacity: 1,
              display: "block",
              // keep in sync
              duration: 0.4,
              stagger: {
                // keep in sync
                each: 0.4,
                repeat: 1,
                yoyo: true,
                from: 0,
              },
            },
            "<",
          )
          // duration tick-mark - keep in sync // the higher the difference between these too, the wider the distribution of the 'curve' will be

          // duration tick-mark
          tl.current.time(6)

          // Set up background color animation with ScrollTrigger
          // Create a separate timeline for the background color
          gsap.timeline({
            scrollTrigger: {
              trigger: animationRootScope.current,
              start: () => (isMobile ? "top top" : "top 150px"),
              end: () => "bottom -1050px",
              scrub: true,
              onUpdate: (self) => {
                // Calculate which color to show based on progress
                const colorIndex = Math.min(
                  Math.floor(self.progress * gradientColors.length),
                  gradientColors.length - 1,
                )
                if (bgRef.current) {
                  bgRef.current.style.backgroundColor =
                    gradientColors[colorIndex]
                }
              },
            },
          })

          gsap.fromTo(
            tl.current,
            { time: 6 },
            {
              // tl.duration() - duration tick-mark
              time: tl.current.duration() - 6,
              ease: "none",
              scrollTrigger: {
                trigger: animationRootScope.current,
                pin: true,
                start: () => (isMobile ? "top top" : "top 150px"),
                end: () => "bottom -1050px",
                invalidateOnRefresh: true,
                scrub: 0.5,
              },
            },
          )
        }
      },
    )

    return () => {
      // cleanup code (optional)
      mm.revert()
      // prevent animation running on other pages
      tl.current.kill()
      // Kill any ScrollTrigger instances to prevent memory leaks
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill())
    }
  }, [gradientColors])

  return (
    <div
      ref={bgRef}
      className={clsx(
        "transition-colors duration-300",
        `bg-[${gradientColors[0]}]`,
      )}
    >
      <section className="py-10 md:py-9 lg:py-7" ref={animationRootScope}>
        <PageGrid id="impact-timeline-trigger">
          <div className="relative col-span-full">
            {sortedItems.map((item, index) => {
              const positionClass =
                index < 10 ? "xs:bottom-0 md:top-0" : "top-0"
              const dateLabel = formatDate(item.date)
              return (
                <React.Fragment key={item.heading}>
                  <div className="flex items-center min-h-[33px]">
                    <div className="md:w-[170px] w-24 h-[3px]">
                      <div className="h-full origin-left tick-mark bg-blue-70 scale-x-[0.2]" />
                    </div>
                    <div className="time-label text-blue-70 headline5 heading2 pl-[18px] md:pl-[22px] lg:pl-[30px] opacity-0">
                      {dateLabel}
                    </div>
                  </div>
                  <div
                    className={clsx(
                      "absolute right-0 p-4 w-1/2 opacity-0 hidden heading-link text-right",
                      positionClass,
                    )}
                  >
                    <p className="w-full text-right body3 md:heading1 lg:headline4 text-blue-70">
                      {item.heading}
                    </p>
                    {item.button && (
                      <Button
                        {...mapContentfulToButtonProps(item.button)}
                        className="inline-block mt-2"
                        target="_blank"
                      />
                    )}
                  </div>
                </React.Fragment>
              )
            })}
          </div>
        </PageGrid>
      </section>
    </div>
  )
}

export default ImpactTimeline
